// src/components/admin/teamTaskbox/TaskKanbanBoard.js
import React, { useState, useEffect } from 'react';
import { Flag, Calendar, Users, Clock, AlertTriangle, Plus } from 'lucide-react';
import teamTasksService from '../../../services/teamTasks.service';
import taskService from '../../../services/task.service';
import { useAuth } from '../../../hooks/useAuth';

const TaskKanbanBoard = ({ tasks, onTaskUpdate, onTaskClick }) => {
    const { user } = useAuth();
    const [draggedTask, setDraggedTask] = useState(null);
    const [dragOverColumn, setDragOverColumn] = useState(null);
    const [loading, setLoading] = useState(false);
    
    const columns = [
        { id: 'pending', title: 'Pending', color: 'bg-gray-100 dark:bg-gray-700', textColor: 'text-gray-700 dark:text-gray-300' },
        { id: 'in_progress', title: 'In Progress', color: 'bg-blue-100 dark:bg-blue-900/30', textColor: 'text-blue-700 dark:text-blue-400' },
        { id: 'under_review', title: 'Under Review', color: 'bg-yellow-100 dark:bg-yellow-900/30', textColor: 'text-yellow-700 dark:text-yellow-400' },
        { id: 'completed', title: 'Completed', color: 'bg-green-100 dark:bg-green-900/30', textColor: 'text-green-700 dark:text-green-400' },
        { id: 'on_hold', title: 'On Hold', color: 'bg-orange-100 dark:bg-orange-900/30', textColor: 'text-orange-700 dark:text-orange-400' }
    ];

    const priorityColors = {
        immediate: 'text-red-600 dark:text-red-400',
        urgent: 'text-orange-600 dark:text-orange-400',
        next_in_line: 'text-yellow-600 dark:text-yellow-400',
        scheduled: 'text-green-600 dark:text-green-400',
        deferred: 'text-gray-600 dark:text-gray-400'
    };

    const getTasksByStatus = (status) => {
        return tasks.filter(task => task.status === status);
    };

    // Helper functions to check user permissions and assignment
    const isUserAssigned = (task) => {
        if (!user?.id) return false;
        return task.assigned_employees?.some(emp => emp.id === user.id) ||
               task.assigned_employee_ids?.includes(user.id) ||
               task.assigned_to === user.id;
    };

    const isTaskCreator = (task) => {
        return task.created_by === user.id;
    };

    const canManageTask = (task) => {
        const userRole = user?.role?.toLowerCase();
        return userRole === 'admin' || userRole === 'manager' || isTaskCreator(task);
    };

    
    const handleDragStart = (e, task) => {
        setDraggedTask(task);
        e.dataTransfer.effectAllowed = 'move';
        e.dataTransfer.setData('text/html', e.target);
        e.target.style.opacity = '0.4';
    };

    const handleDragEnd = (e) => {
        e.target.style.opacity = '1';
        setDraggedTask(null);
        setDragOverColumn(null);
    };

    const handleDragOver = (e) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
        return false;
    };

    const handleDragEnter = (e, columnId) => {
        e.preventDefault();
        setDragOverColumn(columnId);
    };

    const handleDragLeave = (e) => {
        e.preventDefault();
    };

    const handleDrop = async (e, newStatus) => {
        e.preventDefault();
        e.stopPropagation();

        if (!draggedTask || draggedTask.status === newStatus) {
            setDragOverColumn(null);
            return;
        }

        try {
            const response = await teamTasksService.updateTeamTask(draggedTask.id, {
                status: newStatus
            });

            if (response.success) {
                onTaskUpdate();
            }
        } catch (error) {
            console.error('Error updating task status:', error);
            alert('Failed to update task status');
        }

        setDragOverColumn(null);
        return false;
    };

    const formatDate = (dateString) => {
        if (!dateString) return null;
        // Extract YYYY-MM-DD from ISO string
        const dateOnly = dateString.split('T')[0];
        const [year, month, day] = dateOnly.split('-');
        // Create date in local timezone without UTC conversion
        const date = new Date(year, month - 1, day);
        return date.toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric'
        });
    };

    return (
        <div className="flex gap-4 overflow-x-auto pb-4">
            {columns.map(column => {
                const columnTasks = getTasksByStatus(column.id);
                const isDragOver = dragOverColumn === column.id;

                return (
                    <div
                        key={column.id}
                        className="flex-shrink-0 w-80"
                        onDragOver={handleDragOver}
                        onDragEnter={(e) => handleDragEnter(e, column.id)}
                        onDragLeave={handleDragLeave}
                        onDrop={(e) => handleDrop(e, column.id)}
                    >
                        {/* Column Header */}
                        <div className={`${column.color} rounded-t-lg p-4`}>
                            <div className="flex items-center justify-between">
                                <h3 className={`font-semibold ${column.textColor}`}>
                                    {column.title}
                                </h3>
                                <span className={`px-2 py-1 rounded-full text-sm font-medium ${column.textColor} bg-white dark:bg-gray-800`}>
                                    {columnTasks.length}
                                </span>
                            </div>
                        </div>

                        {/* Column Body */}
                        <div
                            className={`bg-gray-50 dark:bg-gray-800 rounded-b-lg p-4 min-h-[600px] space-y-3 transition-all ${
                                isDragOver ? 'ring-2 ring-blue-500 bg-blue-50 dark:bg-blue-900/20' : ''
                            }`}
                        >
                            {columnTasks.length === 0 ? (
                                <div className="flex items-center justify-center h-32 text-gray-400 dark:text-gray-600">
                                    <p className="text-sm">No tasks</p>
                                </div>
                            ) : (
                                columnTasks.map(task => {
                                    const progress = teamTasksService.calculateProgress(task);
                                    const isOverdue = task.overdue_days > 0;

                                    return (
                                        <div
                                            key={task.id}
                                            draggable
                                            onDragStart={(e) => handleDragStart(e, task)}
                                            onDragEnd={handleDragEnd}
                                            onClick={() => onTaskClick(task)}
                                            className="bg-white dark:bg-gray-700 rounded-lg shadow-sm hover:shadow-md transition-all cursor-move p-4 border-2 border-transparent hover:border-blue-300"
                                        >
                                            {/* Task Header */}
                                            <div className="flex items-start justify-between mb-2">
                                                <Flag
                                                    className={`w-4 h-4 ${priorityColors[task.priority]} flex-shrink-0`}
                                                    fill="currentColor"
                                                />
                                                <div className="flex items-center gap-1">
                                                    {isOverdue && (
                                                        <span className="flex items-center gap-1 px-2 py-0.5 bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400 rounded text-xs">
                                                            <AlertTriangle className="w-3 h-3" />
                                                            {task.overdue_days}d
                                                        </span>
                                                    )}
                                                                                                    </div>
                                            </div>

                                            {/* Task Title */}
                                            <h4 className="font-semibold text-gray-900 dark:text-white mb-2 line-clamp-2">
                                                {task.title}
                                            </h4>

                                            {/* Task Description */}
                                            {task.description && (
                                                <p className="text-sm text-gray-600 dark:text-gray-400 line-clamp-2 mb-3">
                                                    {task.description}
                                                </p>
                                            )}

                                            {/* Progress Bar */}
                                            <div className="mb-3">
                                                <div className="flex items-center justify-between text-xs text-gray-600 dark:text-gray-400 mb-1">
                                                    <span>Progress</span>
                                                    <span>{progress}%</span>
                                                </div>
                                                <div className="w-full bg-gray-200 dark:bg-gray-600 rounded-full h-1.5">
                                                    <div
                                                        className={`h-1.5 rounded-full transition-all ${
                                                            progress === 100 ? 'bg-green-600' : 'bg-blue-600'
                                                        }`}
                                                        style={{ width: `${progress}%` }}
                                                    />
                                                </div>
                                            </div>

                                            {/* Task Meta */}
                                            <div className="flex items-center justify-between text-xs text-gray-600 dark:text-gray-400">
                                                <div className="flex items-center gap-3">
                                                    {task.deadline && (
                                                        <div className="flex items-center gap-1">
                                                            <Calendar className="w-3 h-3" />
                                                            {formatDate(task.deadline)}
                                                        </div>
                                                    )}
                                                    <div className="flex items-center gap-1">
                                                        <Users className="w-3 h-3" />
                                                        {task.assigned_employees_count || 0}
                                                    </div>
                                                </div>
                                                {task.estimated_hours && (
                                                    <div className="flex items-center gap-1">
                                                        <Clock className="w-3 h-3" />
                                                        {task.estimated_hours}h
                                                    </div>
                                                )}
                                            </div>

                                            {/* Sub-tasks indicator */}
                                            {(() => {
                                                const { subtasksTotal, subtasksCompleted } = teamTasksService.getSubtaskCounts(task);
                                                return subtasksTotal > 0 && (
                                                    <div className="mt-2 pt-2 border-t border-gray-200 dark:border-gray-600">
                                                        <div className="flex items-center gap-1 text-xs text-gray-600 dark:text-gray-400">
                                                            <span>✓ {subtasksCompleted}/{subtasksTotal} sub-tasks</span>
                                                        </div>
                                                    </div>
                                                );
                                            })()}
                                        </div>
                                    );
                                })
                            )}
                        </div>
                    </div>
                );
            })}
        </div>
    );
};

export default TaskKanbanBoard;
